<template>
  <div class="vivo">
    <div v-if="loading" class="state">{{ $t("providers.loading") }}</div>
    <div v-else-if="error" class="state">{{ $t("providers.error") }}</div>
    <template v-else>
      <div class="row between" style="margin-bottom: 14px">
        <div>
          <div class="pagetitle">{{ $t("providers.title") }}</div>
          <div class="crumb">{{ $t("common.company") }} / {{ $t("providers.crumb") }}</div>
        </div>
      </div>

      <div class="callout" style="margin-bottom: 14px">ℹ️ {{ $t("providers.reference") }}</div>

      <div class="panel">
        <div class="toolbar">
          <div class="searchbar grow">
            🔎
            <input v-model="q" type="text" :placeholder="$t('providers.searchPlaceholder')" />
          </div>
          <button
            type="button"
            class="chip"
            :class="{ on: typeFilter === 'all' }"
            @click="typeFilter = 'all'"
          >
            {{ $t("providers.filters.all") }}
          </button>
          <button
            v-for="c in chips"
            :key="c.type"
            type="button"
            class="chip"
            :class="{ on: typeFilter === c.type }"
            @click="typeFilter = c.type"
          >
            {{ typeLabel(c.type) }}
          </button>
        </div>

        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>{{ $t("providers.cols.provider") }}</th>
                <th>{{ $t("providers.cols.type") }}</th>
                <th>{{ $t("providers.cols.city") }}</th>
                <th>{{ $t("providers.cols.network") }}</th>
                <th>{{ $t("providers.cols.status") }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filtered.length === 0">
                <td colspan="6" class="muted" style="text-align: center; padding: 22px">
                  {{ $t("providers.empty") }}
                </td>
              </tr>
              <template v-for="row in filtered">
                <tr :key="row.id">
                  <td>
                    <div class="u">
                      <span class="avatar" :class="row.avatarCls">{{ row.icon }}</span>
                      {{ row.name }}
                    </div>
                  </td>
                  <td><span class="tag muted">{{ row.typeLabel }}</span></td>
                  <td>{{ row.city }}</td>
                  <td>{{ row.networkName }}</td>
                  <td>
                    <span class="tag" :class="statusTag(row.statusKey)">
                      <span class="dotc"></span> {{ statusLabel(row.statusKey) }}
                    </span>
                  </td>
                  <td>
                    <button type="button" class="btn ghost sm" @click="toggleDetails(row.id)">
                      {{ $t("providers.details") }}
                    </button>
                  </td>
                </tr>
                <tr v-if="expandedId === row.id" :key="row.id + '-d'">
                  <td colspan="6">
                    <div class="note info">
                      <b>{{ row.name }}</b>
                      {{ $t("providers.cols.network") }}: {{ row.networkName }} —
                      {{ $t("providers.detailNote") }}
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="row between" style="padding: 11px 15px">
          <span class="muted xs">{{ countLabel }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { apiGet } from "../../api/vivo/http";
import { companyPath } from "../../api/vivo/session";
import { avatarFor, statusOf, filterProviders, typeChips } from "./providers-vm";

const STATUS_TAG = {
  active: "ok",
  suspended: "review",
};

export default {
  name: "VivoProviders",
  data() {
    return {
      providers: [],
      total: 0,
      q: "",
      typeFilter: "all",
      expandedId: null,
      loading: true,
      error: false,
    };
  },
  async mounted() {
    try {
      // Scope the list to the HR user's own policy network (spec: HR should only
      // see their policy's network, not every provider in the tenant). The
      // policy's networkId comes from the company's own policies; tolerate a gap
      // there (no policy / no networkId) and fall back to the previous unscoped
      // list rather than showing an empty/broken screen.
      let networkId;
      try {
        const policiesRes = await apiGet(companyPath("policies"));
        const policies = (policiesRes && policiesRes.data) || [];
        const withNetwork = policies.find((p) => p && p.networkId);
        networkId = withNetwork ? withNetwork.networkId : undefined;
      } catch (e) {
        networkId = undefined;
      }
      const res = await apiGet("/providers", networkId ? { networkId, limit: 100 } : { limit: 100 });
      this.providers = (res && res.data) || [];
      this.total = res && res.meta ? res.meta.total : this.providers.length;
    } catch (e) {
      this.error = true;
    } finally {
      this.loading = false;
    }
  },
  computed: {
    chips() {
      return typeChips(this.providers);
    },
    enriched() {
      return this.providers.map((p) => {
        const av = avatarFor(p.type);
        return {
          id: p.id,
          name: p.name,
          type: p.type,
          city: p.city,
          networkName: p.networkName,
          typeLabel: this.typeLabel(p.type),
          icon: av.icon,
          avatarCls: av.cls,
          statusKey: statusOf(p),
        };
      });
    },
    filtered() {
      return filterProviders(this.enriched, { q: this.q, type: this.typeFilter });
    },
    countLabel() {
      return this.$t("providers.count", {
        n: this.fInt(this.filtered.length),
        total: this.fInt(this.total),
      });
    },
  },
  methods: {
    fInt(n) {
      return new Intl.NumberFormat(this.$i18n.locale, { numberingSystem: "latn" }).format(n || 0);
    },
    typeLabel(type) {
      return this.$t("providers.types." + type);
    },
    statusLabel(statusKey) {
      return this.$t("providers.status." + statusKey);
    },
    statusTag(statusKey) {
      return STATUS_TAG[statusKey] || "muted";
    },
    toggleDetails(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
  },
};
</script>
